import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { GuarantorDetailsFormType, SmeDirectorBasicType, requiredFieldError } from '@brighte/validation';
import { Box } from '@spark-web/box';
import { Checkbox } from '@spark-web/checkbox';
import { Field } from '@spark-web/field';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { GUARANTOR_DETAILS } from '@utils';

type GuarantorSelectionProps = {
  directors: SmeDirectorBasicType[];
  formControls: UseFormReturn<GuarantorDetailsFormType>;
  name: string;
};

export function GuarantorSelection({ directors, formControls, name }: GuarantorSelectionProps) {
  const { DESCRIPTION, SINGLE_DIRECTOR_MESSAGE } = GUARANTOR_DETAILS;
  const { setValue, watch, formState, trigger } = formControls;
  const isSingleDirector = directors.length === 1;
  const watchGuarantors = (watch(name) || []) as SmeDirectorBasicType[];
  const showFieldError = formState.errors?.enterprise;

  useEffect(() => {
    if (directors.length === 0) return;

    const currentGuarantors = watch(name);
    const syncedGuarantors = directors.map(director => {
      const existingSelection = currentGuarantors?.find(
        selection =>
          selection?.firstName === director.firstName &&
          selection?.lastName === director.lastName &&
          selection?.email === director.email &&
          selection?.isGuarantor,
      );
      return {
        ...director,
        isGuarantor: directors.length === 1 ? true : Boolean(existingSelection?.isGuarantor),
      };
    });

    setValue(name, syncedGuarantors, { shouldValidate: false });
  }, [directors, name, watch, setValue]);

  const handleChange = (index: number) => () => {
    const director = watchGuarantors[index] || directors[index];
    const updatedDirector = {
      ...director,
      isGuarantor: !director?.isGuarantor,
    };
    setValue(`${name}.${index}`, updatedDirector, { shouldValidate: true });
    trigger('enterprise');
  };

  return (
    <Stack gap="xlarge">
      <Field
        label={isSingleDirector ? '' : DESCRIPTION}
        tone={showFieldError ? 'critical' : undefined}
        message={showFieldError ? requiredFieldError('Guarantor') : ''}
      >
        {isSingleDirector ? (
          <Box
            background="primaryLow"
            style={{ height: 150 }}
            justifyContent="center"
            alignItems="center"
            display="flex"
            borderRadius="small"
          >
            <Text weight="bold" align="center">
              {SINGLE_DIRECTOR_MESSAGE}
            </Text>
          </Box>
        ) : (
          <Stack gap="medium">
            {directors.map((director, index) => {
              const fullName = `${director.firstName} ${director.lastName}`;
              const isChecked = watchGuarantors?.[index]?.isGuarantor || false;

              return (
                <Box border="neutral" padding="large" borderRadius="small" key={`${name}-${index}`}>
                  <Checkbox checked={isChecked} onChange={handleChange(index)}>
                    {fullName}
                  </Checkbox>
                </Box>
              );
            })}
          </Stack>
        )}
      </Field>
    </Stack>
  );
}
